/**
 * V5 Position layer — shows the V5 segment-interpolated position
 * for the selected vehicle as a semi-transparent marker with deviation line.
 *
 * Follows the V4PositionLayer pattern: admin-toggled, clickable, with deviation line.
 */

import { memo, useMemo, useRef, useState } from 'react';
import { Source, Layer, useMap } from 'react-map-gl/maplibre';
import type { CircleLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point, LineString } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useSegmentStore } from '../../stores/segmentStore';
import { interpolateSegmentPosition } from '../../utils/segmentInterpolation';
import { interpolatePosition } from '../../utils/interpolation';
import { CROSSHAIR_ICON } from '../../utils/vehicleIcons';
import { useAnimationLoop } from '../../hooks/useAnimationLoop';

const V5_POSITION_SOURCE_ID = 'v5-position-source';
const V5_BACKGROUND_LAYER_ID = 'v5-position-background';
const V5_ICON_LAYER_ID = 'v5-position-icon';
const V5_LINE_SOURCE_ID = 'v5-deviation-source';
const V5_LINE_LAYER_ID = 'v5-deviation-line';

/** Max age for segment data before hiding (15 seconds) */
const SEGMENT_FRESHNESS_MS = 15_000;

interface V5PositionFeatureProperties {
  id: string;
  color_bg: string;
}

interface V5DeviationProperties {
  id: string;
}

const EMPTY_POINT_GEOJSON: FeatureCollection<Point, V5PositionFeatureProperties> = {
  type: 'FeatureCollection',
  features: [],
};

const EMPTY_LINE_GEOJSON: FeatureCollection<LineString, V5DeviationProperties> = {
  type: 'FeatureCollection',
  features: [],
};

export const V5PositionLayer = memo(function V5PositionLayer() {
  useMap();
  const selectedVehicle = useVehicleStore((state) =>
    state.selectedVehicleId ? state.vehicles.get(state.selectedVehicleId) : null
  );
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const showV5Position = useSettingsStore((state) => state.showV5Position);
  const segment = useSegmentStore((state) =>
    selectedVehicleId ? state.segments.get(selectedVehicleId) : undefined
  );

  // 30fps animation loop driven by requestAnimationFrame
  // tick[0] = performance.now() (animation clock), tick[1] = Date.now() (wall clock)
  const [tick, setTick] = useState<[number, number]>(() => [performance.now(), Date.now()]);
  const lastTickRef = useRef(0);
  useAnimationLoop((timestamp) => {
    if (timestamp - lastTickRef.current > 32) { // ~30fps
      lastTickRef.current = timestamp;
      setTick([timestamp, Date.now()]);
    }
  }, showV5Position && !!segment);

  const animNow = tick[0];
  const wallNow = tick[1];

  // Compute V5 position and build GeoJSON for marker
  const pointGeojson = useMemo(() => {
    if (!selectedVehicle || !showV5Position || !segment) {
      return EMPTY_POINT_GEOJSON;
    }

    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_POINT_GEOJSON;
    }

    // Skip rendering if segment data is stale
    if (wallNow - segment.receivedAt > SEGMENT_FRESHNESS_MS) {
      return EMPTY_POINT_GEOJSON;
    }

    const v5Pos = interpolateSegmentPosition(segment, animNow);
    if (!v5Pos) return EMPTY_POINT_GEOJSON;

    const [lat, lon] = v5Pos;

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat],
          },
          properties: {
            id: selectedVehicle.service_journey_id,
            color_bg: segment.color_bg || selectedVehicle.line?.color_bg || '#666666',
          },
        },
      ],
    } as FeatureCollection<Point, V5PositionFeatureProperties>;
  }, [selectedVehicle, showV5Position, segment, animNow, wallNow]);

  // Deviation line from GPS position to V5 position
  const lineGeojson = useMemo(() => {
    if (!selectedVehicle || !showV5Position || !segment) {
      return EMPTY_LINE_GEOJSON;
    }

    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_LINE_GEOJSON;
    }

    // Skip rendering if segment data is stale
    if (wallNow - segment.receivedAt > SEGMENT_FRESHNESS_MS) {
      return EMPTY_LINE_GEOJSON;
    }

    const v5Pos = interpolateSegmentPosition(segment, animNow);
    if (!v5Pos) return EMPTY_LINE_GEOJSON;

    const [v5Lat, v5Lon] = v5Pos;

    // Get interpolated GPS position for smooth line
    const [gpsLat, gpsLon] = interpolatePosition(
      selectedVehicle.latitude,
      selectedVehicle.longitude,
      selectedVehicle.targetLatitude,
      selectedVehicle.targetLongitude,
      selectedVehicle.lastUpdateTime,
      wallNow
    );

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [gpsLon, gpsLat],
              [v5Lon, v5Lat],
            ],
          },
          properties: {
            id: selectedVehicle.service_journey_id,
          },
        },
      ],
    } as FeatureCollection<LineString, V5DeviationProperties>;
  }, [selectedVehicle, showV5Position, segment, animNow, wallNow]);

  // V5 marker style — subtle purple backdrop for the crosshair
  const backgroundLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: V5_BACKGROUND_LAYER_ID,
      type: 'circle',
      source: V5_POSITION_SOURCE_ID,
      paint: {
        'circle-radius': 14,
        'circle-color': ['get', 'color_bg'],
        'circle-stroke-color': '#7c3aed',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 0.6,
        'circle-opacity': 0.3,
      },
    }),
    []
  );

  // V5 crosshair icon — purple
  const iconLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: V5_ICON_LAYER_ID,
      type: 'symbol',
      source: V5_POSITION_SOURCE_ID,
      layout: {
        'icon-image': CROSSHAIR_ICON,
        'icon-size': 2.0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
      paint: {
        'icon-color': '#a855f7', // Purple crosshair
        'icon-opacity': 0.9,
      },
    }),
    []
  );

  // Deviation line style — purple dashed, more visible
  const lineLayerStyle: LineLayerSpecification = useMemo(
    () => ({
      id: V5_LINE_LAYER_ID,
      type: 'line',
      source: V5_LINE_SOURCE_ID,
      paint: {
        'line-color': '#a855f7',
        'line-width': 2.5,
        'line-opacity': 0.7,
        'line-dasharray': [4, 3],
      },
    }),
    []
  );

  // V5 marker is visual-only (not clickable) — V1 GPS is the only true position
  if (!showV5Position || !selectedVehicleId) return null;

  return (
    <>
      {/* Deviation line (rendered below marker) */}
      <Source id={V5_LINE_SOURCE_ID} type="geojson" data={lineGeojson}>
        <Layer {...lineLayerStyle} />
      </Source>

      {/* V5 position marker: background circle + crosshair icon */}
      <Source id={V5_POSITION_SOURCE_ID} type="geojson" data={pointGeojson}>
        <Layer {...backgroundLayerStyle} />
        <Layer {...iconLayerStyle} />
      </Source>
    </>
  );
});
